import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, MessageCircle, Loader2, Sparkles, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BACKEND_URL } from "@/lib/api";

const QUICK_PROMPTS = [
  "Someone is having a heart attack. What do I do?",
  "There's a fire in my building. Escape steps?",
  "I feel unsafe walking home. What now?",
  "How to help someone bleeding heavily",
  "Small child choked on food - immediate steps",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "I am Guardian AI. I provide calm, step-by-step help in any emergency. Ask me anything about safety - medical, fire, road, personal." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setInput("");
    setLoading(true);

    try {
      const token = localStorage.getItem("g360_token");
      const resp = await fetch(`${BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Parse SSE frames
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const chunk of parts) {
          const lines = chunk.split("\n");
          const event = lines.find((l) => l.startsWith("event: "))?.replace("event: ", "");
          const dataLines = lines.filter((l) => l.startsWith("data: ")).map((l) => l.slice(6));
          const data = dataLines.join("\n");
          let parsed;
          try { parsed = JSON.parse(data); } catch { parsed = data; }
          if (event === "done") {
            setSessionId(parsed);
          } else if (parsed !== undefined && parsed !== null) {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + parsed };
              return next;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: "Sorry, I'm having trouble responding right now. Please call your local emergency number." };
        return next;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto flex flex-col" style={{ minHeight: "calc(100vh - 4rem)" }}>
      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--secondary))] mb-2 font-semibold">Guardian AI</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter flex items-center gap-3">
          Emergency assistant
          <Sparkles className="w-6 h-6 text-[hsl(var(--accent))]" />
        </h1>
        <p className="text-muted-foreground mt-1">Certified guidance, powered by Claude Sonnet. Always call emergency services first for critical situations.</p>
      </div>

      <div className="flex-1 rounded-2xl border border-border/70 bg-card overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`msg-${i}`}
            >
              {m.role === "assistant" && (
                <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))] grid place-items-center mr-2 shrink-0 self-start">
                  <Shield className="w-4 h-4 text-white" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[hsl(var(--primary))] text-white" : "bg-muted"}`}>
                {m.content || (loading && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin" /> : "")}
              </div>
            </motion.div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="border-t border-border/60 p-4">
          {messages.length <= 2 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {QUICK_PROMPTS.map((p) => (
                <button
                  key={p} onClick={() => send(p)} disabled={loading}
                  className="text-xs px-3 py-1.5 rounded-full border border-border/70 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
                  data-testid="quick-prompt"
                >
                  {p}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <Input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Guardian AI..."
              disabled={loading}
            />
            <Button data-testid="ai-send-btn" type="submit" disabled={loading || !input.trim()} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
